import translate from "../../../i18n/translate";
import React from "react";
import { formatDateFullYear } from "../../common/helper";
import ApiError from "../../common/elements/ApiError";

export interface ParticipatingTableProps {
  orgHierarchy: any;
  leads: any;
  heading: any;
  planStatus: any;
  apiError: boolean;
}

export default function ParticipatingTable({
  orgHierarchy,
  leads,
  heading,
  planStatus,
  apiError
}: ParticipatingTableProps): JSX.Element {
  const renderHierarchy = (node: any) => {
    return (
      <React.Fragment key={`${node.uuid}-hierarchy`}>
        <Node node={node} leads={leads} />

        {node.children &&
          node.children.map((child: any) => {
            return renderHierarchy(child);
          })}
      </React.Fragment>
    );
  };

  const TableBody = (props: any) => {
    return <tbody className="databody">{props.children}</tbody>;
  };

  const Table = (props: any) => {
    return <table className="data-table -shadow">{props.children}</table>;
  };

  const TableHeader = (props: any) => {
    return (
      <thead className="dataheader">
        <tr className="dataheaderrow">
          {props.columns.map((column: string, index: number) => {
            return (
              <th
                className="dataheadercell"
                data-th={column}
                key={`column-${index}`}
              >
                {column}
              </th>
            );
          })}
        </tr>
      </thead>
    );
  };

  const Node = (props: any) => {
    const { node, leads } = props;
    return (
      <>
        {node.showStats ? (
          <CompleteRow node={node} lead={leads[node["uuid"]]} />
        ) : (
          <NoStatsRow node={node} />
        )}
      </>
    );
  };

  const NoStatsRow = (props: any) => {
    const { node } = props;
    return (
      <tr key={`${node.uuid}-datarow`}>
        <td className={`datacell level-${node.level}`}>{node.name}</td>
        <td className="datacell"></td>
        <td className="datacell"></td>
        <td className="datacell"></td>
      </tr>
    );
  };

  const CompleteRow = (props: any) => {
    const { node, lead } = props;
    const leadName = () => {
      if (typeof lead != "undefined") {
        return lead.leads.map((l: any, index: number) => {
          return (
            <div className="leadcell" key={`lead-${index}-${node.uuid}`}>
              {l}
            </div>
          );
        });
      }
    };

    return (
      <tr key={`${node.uuid}-datarow`}>
        <td className={`datacell level-${node.level} organization-cell`}>
          {node.name}
        </td>
        <StatusCell status={node.status} />
        <StatsCell node={node} />
        <td className="datacell">{leadName()}</td>
      </tr>
    );
  };

  const StatsCell = (props: any) => {
    const { node } = props;

    const stats = () => {
      return node.updatedAt
        ? formatDateFullYear(node.updatedAt)
        : translate("app.na");
    };

    return <td className="datacell stats-cell">{stats()}</td>;
  };

  const StatusCell = (props: any) => {
    const content = () => {
      switch (props.status) {
        case "not_mapped":
          return (
            <div className="not-started-status">
              {translate("app.drilldown.notMapped")}
            </div>
          );
        case "not_started":
          return (
            <div className="not-started-status leadcell">
              {translate("app.drilldown.notStarted")}
            </div>
          );
        case "mapped":
          return (
            <div className="success-status -mapped leadcell">
              <div className="title-text leadcell">
                {translate("app.drilldown.mapped")}
              </div>
            </div>
          );
        case "submitted":
          return (
            <div className="success-status status-export-table leadcell">
              <span className="title-text leadcell">
                {translate("app.drilldown.submitted")}
              </span>
            </div>
          );
        case "in_progress":
          let inProgress =
            planStatus == 1
              ? translate("app.drilldown.inComplete")
              : translate("app.drilldown.inProgress");
          return (
            <div className="in-progress-status-export leadcell">
              <span className="leadcell">{inProgress}</span>
            </div>
          );
      }
    };
    return <td className="datacell status-cell">{content()}</td>;
  };

  return (
    <>
      <div className="table-heading">{heading}</div>
      {apiError ? (
        <ApiError containerClasses="api-error" />
      ) : (
        <div className="org-table-export">
          <Table>
            <TableHeader
              columns={[
                translate("app.drilldown.organization"),
                translate("app.drilldown.status"),
                translate("app.drilldown.lastUpdated"),
                translate("app.drilldown.lead")
              ]}
            />
            <TableBody>{renderHierarchy(orgHierarchy)}</TableBody>
          </Table>
        </div>
      )}
    </>
  );
}
